import { useState, useEffect } from 'react';
import { Stack, NumberInput, Group, Button, SimpleGrid, Fieldset } from '@mantine/core';
import { useLoansContext } from '@/context/loans-context-definition';

interface AssumptionsPanelProps {
  onApply: () => void;
  onCancel: () => void;
}

export function AssumptionsPanel({ onApply, onCancel }: AssumptionsPanelProps) {
  const {
    inflationRate,
    setInflationRate,
    incomeGrowthRate,
    setIncomeGrowthRate,
    refiInterestRate,
    setRefiInterestRate,
    refiPeriodYears,
    setRefiPeriodYears,
    taxRate,
    setTaxRate,
  } = useLoansContext();

  const [localRefiRate, setLocalRefiRate] = useState<number>(refiInterestRate);
  const [localRefiTerm, setLocalRefiTerm] = useState<number>(refiPeriodYears);
  const [localTaxRate, setLocalTaxRate] = useState<number>(taxRate);
  const [localInflationRate, setLocalInflationRate] = useState<number>(inflationRate);
  const [localIncomeGrowthRate, setLocalIncomeGrowthRate] = useState<number>(incomeGrowthRate);

  useEffect(() => {
    setLocalRefiRate(refiInterestRate);
    setLocalRefiTerm(refiPeriodYears);
    setLocalTaxRate(taxRate);
    setLocalInflationRate(inflationRate);
    setLocalIncomeGrowthRate(incomeGrowthRate);
  }, [refiInterestRate, refiPeriodYears, taxRate, inflationRate, incomeGrowthRate]);

  const handleApplyChanges = () => {
    setRefiInterestRate(localRefiRate);
    setRefiPeriodYears(localRefiTerm);
    setTaxRate(localTaxRate);
    setInflationRate(localInflationRate);
    setIncomeGrowthRate(localIncomeGrowthRate);
    onApply();
  };

  const handleCancel = () => {
    setLocalRefiRate(refiInterestRate);
    setLocalRefiTerm(refiPeriodYears);
    setLocalTaxRate(taxRate);
    setLocalInflationRate(inflationRate);
    setLocalIncomeGrowthRate(incomeGrowthRate);
    onCancel();
  };

  return (
    <Fieldset legend="Adjust assumptions">
      <Stack gap="sm">
        <SimpleGrid cols={2} verticalSpacing="xs">
          <NumberInput
            label="Inflation rate (%)"
            value={localInflationRate * 100}
            onChange={(value) => {
              const num = Number(value);
              setLocalInflationRate(isNaN(num) ? 0 : num / 100);
            }}
            min={0}
            max={10}
            decimalScale={0}
            suffix="%"
            size="sm"
          />

          <NumberInput
            label="Income growth rate (%)"
            value={localIncomeGrowthRate * 100}
            onChange={(value) => {
              const num = Number(value);
              setLocalIncomeGrowthRate(isNaN(num) ? 0 : num / 100);
            }}
            min={0}
            max={10}
            decimalScale={0}
            suffix="%"
            size="sm"
          />

          <NumberInput
            label="Tax bracket (%)"
            value={localTaxRate * 100}
            onChange={(value) => {
              const num = Number(value);
              setLocalTaxRate(isNaN(num) ? 0 : num / 100);
            }}
            min={0}
            max={50}
            decimalScale={1}
            suffix="%"
            size="sm"
          />

          <NumberInput
            label="Refinance interest rate (%)"
            value={localRefiRate * 100}
            onChange={(value) => {
              const num = Number(value);
              setLocalRefiRate(isNaN(num) ? 7 : num / 100);
            }}
            min={0}
            max={20}
            decimalScale={2}
            suffix="%"
            size="sm"
          />

          <NumberInput
            label="Refinance term (years)"
            value={localRefiTerm}
            onChange={(value) => {
              const num = Number(value);
              setLocalRefiTerm(isNaN(num) ? 30 : num);
            }}
            min={5}
            max={30}
            size="sm"
          />
        </SimpleGrid>

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={handleCancel} size="sm">
            Cancel
          </Button>
          <Button onClick={handleApplyChanges} size="sm">
            Apply changes
          </Button>
        </Group>
      </Stack>
    </Fieldset>
  );
}
